var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { createPopper } from '@popperjs/core';
import { AnalyticsTrigger } from '@core-front/analytics';
import { getOffsetFromParent } from '@core-front/helper';
import { useLabels } from '@core-front/label-provider';
import { isSuggestionGroup } from '@core-front/suggestion';
import { useTheme } from '@core-front/theme-provider';
const SuggestionFieldCore = (props) => {
    const labels = useLabels();
    const { portalRoot } = useTheme();
    return (React.createElement(AnalyticsTrigger, { trigger: [
            {
                callback: 'onSelect',
                id: `Suggestion - Choose - ${props.id}`,
                elementId: `${props.id}`,
                elementType: 'suggestion',
                interactionType: 'select',
            },
            {
                callback: 'onShow',
                id: `Suggestion - Open - ${props.id}`,
                elementId: `${props.id}`,
                elementType: 'suggestion',
                interactionType: 'open',
            },
        ] },
        React.createElement(SuggestionFieldCoreInner, Object.assign({}, props, { labels: labels, portalContainer: props.portalContainer || portalRoot }))));
};
export default SuggestionFieldCore;
export class SuggestionFieldCoreInner extends React.PureComponent {
    constructor(props) {
        super(props);
        this.referenceElement = null;
        this.suggestion = null;
        this.itemElements = [];
        this.popper = null;
        this.blurTimeout = 0;
        this.getZIndex = () => {
            /* istanbul ignore else */
            if (this.referenceElement) {
                const inHeader = this.referenceElement.closest('.header') !== null;
                const inModal = this.referenceElement.closest('.modal') !== null;
                if (inHeader || inModal) {
                    return 30;
                }
            }
            return 19;
        };
        this.disablePopper = () => {
            if (this.popper) {
                this.popper.destroy();
                this.popper = null;
            }
        };
        this.onFocus = () => {
            if (this.value || this.props.showSuggestionsOnFocus) {
                setTimeout(() => this.show(), 150);
            }
            if (this.props.onFocus) {
                this.props.onFocus();
            }
        };
        this.hideOnBlur = () => {
            // hide suggestions on blur after timeout to ensure if user clicks suggestion with mouse it will not hide suggestions before click goes through
            window.clearTimeout(this.blurTimeout);
            this.blurTimeout = window.setTimeout(() => {
                if (this.suggestion && !this.suggestion.contains(document.activeElement)) {
                    this.hide();
                }
                else if (this.state.isVisible) {
                    // recursive if user clicks for a long time :)
                    this.hideOnBlur();
                }
            }, 100);
        };
        this.onBlur = (event) => {
            this.hideOnBlur();
            if (this.props.onBlur) {
                this.props.onBlur(event);
            }
        };
        this.onKeyDown = (event) => {
            const { currentIndex, isVisible } = this.state;
            switch (event.key) {
                case 'ArrowDown':
                    if (isVisible) {
                        // select first or next element in suggestions list if suggestions list is visible
                        event.preventDefault();
                        this.selectValue(currentIndex + 1);
                    }
                    return;
                case 'ArrowUp':
                    if (isVisible) {
                        // select last or prev element in suggestions list if suggestions list is visible
                        event.preventDefault();
                        this.selectValue(currentIndex - 1);
                    }
                    return;
                case 'Enter':
                    if (this.inSuggestion) {
                        // if visual focus is in suggestions list:
                        // select focused element from suggestions list
                        // hide suggestions list
                        // prevent default so this would not submit form automatically
                        event.preventDefault();
                        const currentItem = this.cleanItems[currentIndex];
                        this.onChange(currentItem.label);
                        /* istanbul ignore else */
                        if (this.props.onSelect) {
                            this.props.onSelect(currentItem);
                        }
                        this.hide();
                    }
                    return;
                case 'Tab':
                case 'Escape':
                case 'Esc':
                    // hide suggestions list when user tabs out of field or presses esc key
                    this.hide();
                    return;
                case 'ArrowLeft':
                case 'ArrowRight':
                case 'Home':
                case 'End':
                    // reset active descendant value in listbox if user moves cursor in field
                    this.resetValue();
                    return;
            }
            /* istanbul ignore else */
            if (this.props.onKeyDown) {
                this.props.onKeyDown(event);
            }
        };
        this.onClick = (event, clickedItem) => {
            event.preventDefault();
            this.onChange(clickedItem.label);
            /* istanbul ignore else */
            if (this.props.onSelect) {
                this.props.onSelect(clickedItem);
            }
            this.hide();
        };
        this.onChange = (value) => {
            if (!this.isValueControlled()) {
                this.setState({
                    value,
                });
            }
            if (this.props.onChange) {
                this.props.onChange(value);
            }
            if (value || this.props.showSuggestionsOnFocus) {
                this.show();
            }
            else {
                this.hide();
            }
            this.resetValue();
        };
        this.onScroll = () => {
            const elem = this.suggestion;
            if (this.props.onScrollToEnd && elem && elem.scrollHeight - elem.scrollTop <= elem.offsetHeight) {
                this.props.onScrollToEnd();
            }
        };
        this.renderAssistiveText = () => {
            const { isVisible } = this.state;
            return (React.createElement("span", { "aria-live": "polite", role: "status", className: "sr-only suggestionfield__assistive-text" }, isVisible && this.getAssistiveText(this.cleanItems.length)));
        };
        this.getContent = () => this.props.contentRenderer ? (React.createElement(React.Fragment, null,
            this.renderAssistiveText(),
            this.props.contentRenderer())) : (this.renderAssistiveText());
        this.onClickOutside = this.onClickOutside.bind(this);
        this.state = {
            currentIndex: -1,
            isMounted: false,
            isVisible: false,
            value: props.defaultValue || '',
            popperStyle: {},
        };
    }
    componentDidMount() {
        document.addEventListener('mousedown', this.onClickOutside);
        this.setState({
            isMounted: true,
        });
    }
    componentDidUpdate(prevProps, prevState) {
        if (prevProps.isLoading === false && this.props.isLoading === true) {
            this.scrollToEnd();
        }
        const haveItemsSurfaced = (!prevProps.items || (prevProps.items && prevProps.items.length === 0)) && this.props.items && this.props.items.length > 0;
        const haveItemsDisappeared = (!prevProps.items || (prevProps.items && prevProps.items.length > 0)) && this.props.items && this.props.items.length === 0;
        if ((this.state.isVisible && !prevState.isVisible) || (this.state.isVisible && haveItemsSurfaced)) {
            this.initPopper();
        }
        else if ((!this.state.isVisible && prevState.isVisible) || haveItemsDisappeared) {
            this.disablePopper();
        }
    }
    componentWillUnmount() {
        if (this.popper) {
            this.popper.destroy();
        }
        document.removeEventListener('mousedown', this.onClickOutside);
        window.clearTimeout(this.blurTimeout);
    }
    get value() {
        return this.isValueControlled(this.props.value) ? this.props.value : this.state.value;
    }
    isValueControlled(value = this.props.value) {
        const isDefined = typeof value !== 'undefined';
        if (this.props.isReadOnly || this.props.isDisabled) {
            return isDefined;
        }
        return !!this.props.onChange && isDefined;
    }
    scrollToEnd() {
        /* istanbul ignore else */
        if (this.suggestion) {
            const scrollHeight = this.suggestion.scrollHeight;
            const height = this.suggestion.clientHeight;
            const maxScrollTop = scrollHeight - height;
            this.suggestion.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
        }
    }
    get items() {
        const items = this.props.items || [];
        let globalIndex = 0;
        return items.map(item => {
            if (isSuggestionGroup(item)) {
                return Object.assign(Object.assign({}, item), { items: item.items.map((groupItem) => {
                        globalIndex++;
                        const currentIndex = globalIndex - 1;
                        return Object.assign(Object.assign({}, groupItem), { id: groupItem.id || this.getSuggestionItemId(currentIndex), isFocused: this.state.currentIndex === currentIndex, itemRef: (el) => el && (this.itemElements[currentIndex] = el), onClick: event => {
                                this.onClick(event, groupItem);
                                if (groupItem.onClick) {
                                    groupItem.onClick(event);
                                }
                            }, role: 'option', 'aria-selected': this.state.currentIndex === currentIndex });
                    }) });
            }
            else {
                globalIndex++;
                const currentIndex = globalIndex - 1;
                return Object.assign(Object.assign({}, item), { id: item.id || this.getSuggestionItemId(currentIndex), isFocused: this.state.currentIndex === currentIndex, itemRef: (el) => el && (this.itemElements[currentIndex] = el), onClick: event => {
                        this.onClick(event, item);
                        if (item.onClick) {
                            item.onClick(event);
                        }
                    }, role: 'option', 'aria-selected': this.state.currentIndex === currentIndex });
            }
        });
    }
    get cleanItems() {
        const items = this.props.items || [];
        return items.reduce((accumulator, item) => {
            if (isSuggestionGroup(item)) {
                return [...accumulator, ...item.items];
            }
            else {
                return [...accumulator, item];
            }
        }, []);
    }
    get listBoxId() {
        return `${this.props.id}-suggestion`;
    }
    get activeDescendant() {
        var _a;
        const { currentIndex } = this.state;
        return this.inSuggestion ? ((_a = this.cleanItems[currentIndex]) === null || _a === void 0 ? void 0 : _a.id) || this.getSuggestionItemId(currentIndex) : undefined;
    }
    get inSuggestion() {
        return this.state.isVisible && this.state.currentIndex >= 0;
    }
    getSuggestionItemId(index) {
        return `${this.listBoxId}-item-${index}`;
    }
    initPopper() {
        const { offsetY } = this.props;
        if (this.suggestion && this.referenceElement && !this.popper) {
            this.popper = createPopper(this.referenceElement, this.suggestion, {
                modifiers: [
                    { name: 'applyStyles', enabled: false },
                    { name: 'flip', enabled: false },
                    {
                        name: 'offset',
                        options: {
                            offset: [0, offsetY ? offsetY : -1],
                        },
                    },
                    {
                        name: 'updateState',
                        enabled: true,
                        phase: 'write',
                        requires: ['computeStyles'],
                        fn: ({ state }) => this.updatePopperStyle(state),
                        effect: ({ state }) => this.updatePopperStyle(state),
                    },
                ],
                placement: 'bottom-start',
            });
        }
    }
    updatePopperStyle(state) {
        const popperStyle = Object.assign(Object.assign({}, state.styles.popper), { zIndex: this.getZIndex() });
        if (this.referenceElement && !this.props.fullWidth) {
            popperStyle.width = this.referenceElement.offsetWidth;
        }
        this.setState({ popperStyle });
    }
    onClickOutside(event) {
        if (!(event.target instanceof Node) || (this.referenceElement && this.referenceElement.contains(event.target))) {
            return;
        }
        if ((this.suggestion && !this.suggestion.contains(event.target)) || (!this.suggestion && this.state.isVisible === true)) {
            this.hide();
        }
    }
    show() {
        if (!this.state.isVisible) {
            this.setState({
                isVisible: true,
            });
            if (this.props.onShow) {
                this.props.onShow();
            }
        }
    }
    hide() {
        if (this.state.isVisible) {
            this.setState({
                isVisible: false,
            });
        }
        this.resetValue();
    }
    selectValue(nextIndex) {
        const itemCount = this.cleanItems.length;
        let actualNextIndex = nextIndex;
        if (nextIndex > itemCount - 1) {
            actualNextIndex = 0;
        }
        else if (nextIndex < 0) {
            actualNextIndex = itemCount - 1;
        }
        this.setState({
            currentIndex: actualNextIndex,
        });
        this.scrollToElement(actualNextIndex);
    }
    resetValue() {
        this.setState({
            currentIndex: -1,
        });
    }
    scrollToElement(elementIndex) {
        if (!this.suggestion) {
            return;
        }
        const selectedElement = this.itemElements[elementIndex];
        const offset = getOffsetFromParent(selectedElement, this.suggestion);
        if (selectedElement && this.suggestion) {
            const containerOffsetTop = this.suggestion.scrollTop;
            const elementTopEdgeYPos = offset.top;
            const elementBottomEdgeYPos = elementTopEdgeYPos - this.suggestion.offsetHeight + selectedElement.offsetHeight;
            if (elementBottomEdgeYPos > containerOffsetTop) {
                this.suggestion.scrollTop = elementBottomEdgeYPos;
            }
            else if (elementTopEdgeYPos < containerOffsetTop) {
                this.suggestion.scrollTop = elementTopEdgeYPos;
            }
        }
    }
    getAssistiveText(count) {
        const { labels } = this.props;
        switch (count) {
            case 0:
                return labels.get('suggestionField.noResultsFound');
            case 1:
                return labels.get('suggestionField.oneResultFound');
            default:
                return labels.get('suggestionField.resultsFound', {
                    count: count.toString(),
                });
        }
    }
    renderSuggestion() {
        if (!this.state.isVisible || (this.items.length === 0 && !this.props.isLoading)) {
            return null;
        }
        const { Suggestion } = this.props.components;
        const listRef = (el) => (this.suggestion = el);
        return (React.createElement(Suggestion, { className: this.props.suggestionClassName, style: this.state.popperStyle, listRef: listRef, items: this.items, highlightedValue: this.value, isLoading: this.props.isLoading, additionalContent: this.props.suggestionContent, onScroll: this.onScroll, role: "listbox", "aria-labelledby": `${this.props.id}-label` }));
    }
    render() {
        const _a = this.props, { components: { TextField }, items, onSelect, onScrollToEnd, onShow, isLoading, suggestionClassName, fullWidth, offsetY, suggestionContent, portalContainer, inputRef, input } = _a, rest = __rest(_a, ["components", "items", "onSelect", "onScrollToEnd", "onShow", "isLoading", "suggestionClassName", "fullWidth", "offsetY", "suggestionContent", "portalContainer", "inputRef", "input"]);
        return (React.createElement(React.Fragment, null,
            React.createElement(TextField, Object.assign({}, rest, { value: this.value, inputRef: (el) => {
                    this.referenceElement = el;
                    if (inputRef) {
                        inputRef(el);
                    }
                }, onKeyDown: this.onKeyDown, onChange: this.onChange, onFocus: this.onFocus, onBlur: this.onBlur, input: Object.assign(Object.assign({ autoComplete: 'off' }, input), { 'aria-autocomplete': 'list', 'aria-controls': this.state.isVisible ? this.listBoxId : undefined, 'aria-activedescendant': this.activeDescendant }), innerAttributes: {
                    role: 'combobox',
                    'aria-expanded': this.state.isVisible ? 'true' : 'false',
                    'aria-owns': this.state.isVisible ? this.listBoxId : undefined,
                    'aria-haspopup': 'listbox',
                }, contentRenderer: this.getContent })),
            this.state.isMounted ? ReactDOM.createPortal(this.renderSuggestion(), portalContainer || document.body) : this.renderSuggestion()));
    }
}
